import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { EASE, SectionTag, MaskedLine } from "./Motion";

const chapters = [
  {
    num: "01",
    title: "Attention.",
    text: "Reach without resonance is noise. We engineer moments people actually stop for — then we measure what the pause was worth.",
  },
  {
    num: "02",
    title: "Trust.",
    text: "Creators are not inventory. Brands are not wallets. We broker relationships, not transactions — and both sides stay for years.",
  },
  {
    num: "03",
    title: "Value.",
    text: "Money doubled is money gambled. Value built compounds — in audiences, in reputation, in revenue that doesn't vanish overnight.",
  },
];

export default function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  const strikeW = useTransform(scrollYProgress, [0.08, 0.32], ["0%", "100%"]);
  const oldOpacity = useTransform(scrollYProgress, [0.3, 0.5], [1, 0.12]);
  const oldScale = useTransform(scrollYProgress, [0.3, 0.5], [1, 0.96]);
  const oldFilter = useTransform(scrollYProgress, [0.3, 0.55], ["blur(0px)", "blur(6px)"]);
  const newClip = useTransform(scrollYProgress, [0.5, 0.82], ["inset(0 100% 0 0)", "inset(0 0% 0 0)"]);
  const newY = useTransform(scrollYProgress, [0.5, 0.82], [40, 0]);

  return (
    <section data-testid="manifesto-section" className="relative">
      <div className="mx-auto max-w-7xl px-6 py-28 sm:px-8 sm:py-36 lg:px-16">
        <SectionTag index="05" label="The Manifesto" testid="manifesto-tag" />
        <h2 className="mt-6 max-w-3xl text-4xl font-bold tracking-tight text-neutral-50 sm:text-5xl">
          <MaskedLine>Principles we print</MaskedLine>
          <MaskedLine delay={0.12} innerClassName="font-serif italic font-medium text-gold-grad">
            in gold.
          </MaskedLine>
        </h2>

        <div className="mt-16 grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-8">
          {chapters.map((c, i) => (
            <motion.div
              key={c.num}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.9, ease: EASE, delay: i * 0.12 }}
              className="border-t border-white/10 pt-8"
              data-testid={`manifesto-chapter-${c.num}`}
            >
              <div className="flex items-baseline gap-4">
                <span className="font-serif text-6xl italic text-gold/30">{c.num}</span>
                <span className="font-mono text-[10px] tracking-[0.3em] text-neutral-500">
                  CHAPTER
                </span>
              </div>
              <h3 className="mt-4 font-serif text-3xl italic text-gold-grad">{c.title}</h3>
              <p className="mt-4 text-sm leading-relaxed text-neutral-400 sm:text-base">{c.text}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <div ref={ref} className="relative h-[260vh]">
        <div className="sticky top-0 flex h-screen flex-col items-center justify-center overflow-hidden px-6">
          <div className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/[0.06] blur-[120px]" />
          <motion.div style={{ opacity: oldOpacity, scale: oldScale, filter: oldFilter }} className="relative text-center">
            <div className="relative inline-block">
              <h2 className="text-4xl font-bold tracking-tight text-neutral-500 sm:text-6xl lg:text-7xl">
                WE DON'T DOUBLE
                <br />
                MONEY
              </h2>
              <motion.div
                style={{ width: strikeW }}
                className="absolute left-0 top-1/2 h-[3px] bg-gold shadow-[0_0_16px_rgb(var(--gold)_/_0.7)]"
                data-testid="manifesto-strike-line"
              />
            </div>
          </motion.div>
          <motion.div
            style={{ clipPath: newClip, y: newY }}
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
          >
            <h2
              className="text-center font-serif text-5xl italic text-gold-grad sm:text-7xl lg:text-8xl"
              data-testid="manifesto-transformation-text"
            >
              We build value.
            </h2>
          </motion.div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 pb-28 sm:px-8 sm:pb-36 lg:px-16">
        <div className="mx-auto max-w-4xl text-center">
          <h3
            data-testid="manifesto-disclaimer"
            className="text-2xl font-extrabold uppercase leading-snug tracking-tight text-neutral-100 sm:text-4xl"
          >
            <MaskedLine>We are not a trading,</MaskedLine>
            <MaskedLine delay={0.12}>investing or money-doubling</MaskedLine>
            <MaskedLine delay={0.24} innerClassName="text-gold-grad">
              company.
            </MaskedLine>
          </h3>
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, amount: 0.8 }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.5 }}
            className="mx-auto mt-8 h-[3px] w-40 bg-gradient-to-r from-transparent via-gold to-transparent"
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.65 }}
            className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-neutral-500 sm:text-base"
          >
            Every rupee you spend buys real work — reach, deals and product. Returns come from
            what we build together, never from a promise.
          </motion.p>
        </div>
      </div>
    </section>
  );
}
